import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Wand2 } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { formatApiErrorDetail } from "./adminApi";
import { LogoMark } from "../components/Logo";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/painel" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/painel");
    } catch (err) {
      if (!err.response) {
        setError("Não foi possível contactar o servidor. Verifique a sua ligação e tente novamente dentro de instantes.");
      } else if (err.response.data?.detail == null) {
        setError(`O servidor respondeu com um erro (${err.response.status}). Tente novamente dentro de instantes.`);
      } else {
        setError(formatApiErrorDetail(err.response.data.detail));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="admin-dark flex min-h-screen items-center justify-center bg-[#0a0a0a] px-5 text-white">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#121212] p-8">
        <div className="flex flex-col items-center text-center">
          <LogoMark className="h-12 w-12" />
          <h1 className="mt-4 font-display text-2xl font-medium text-white">Painel de Administração</h1>
          <p className="mt-1 text-xs text-white/40">Magnolia Lunar</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-gold"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Password</label>
            <div className="relative">
              <input
                data-testid="admin-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 pr-11 text-sm text-white outline-none transition-colors focus:border-gold"
              />
              <button
                type="button"
                data-testid="admin-password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Ocultar password" : "Mostrar password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-gold"
              >
                {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
              </button>
            </div>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-gold-light disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Entrar
          </button>
          {process.env.NODE_ENV !== "production" && (
            <button
              type="button"
              data-testid="admin-autofill-button"
              onClick={() => {
                setEmail("admin@magnolialunar.pt");
                setPassword("MagnoliaLunar2026!");
                setError("");
              }}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-xs uppercase tracking-[0.25em] text-white/60 transition-colors hover:border-gold hover:text-gold"
            >
              <Wand2 className="h-4 w-4" strokeWidth={1.5} />
              Preencher credenciais (dev)
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
